import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  Page,
  Button,
  Layout,
  Card,
  Select,
  FormLayout,
  TextField,
  Checkbox,
  Spinner,
  Text,
} from "@shopify/polaris";
import withRouter from "helpers/withRouter";
import _ from "lodash";
import GoBackPageNavigation from '../../components/GoBackPageNavigation/GoBackPageNavigation'
import { fetchRegNumbers } from "../../redux/features/registration/registrationSlice";
import { fetchCountries, fetchCurrency } from "../../redux/features/other/otherSlice";
import Modal from "components/Modal";
import Payment from "../Payment";
import PageHelmet from "components/PageHelmet";
import { getYears } from "utils/years";
import { createCustomDispatch } from "helpers/customDispatch";

const years = getYears({
  yearsBefore: 1,
  yearsAfter: 0,
  showDefaultValue: false,
  reverseOrder: true,
});

function VATPayment(props) {
  const { regNumbers, fetchingRegNumbers, t, fetchRegNumbers, fetchCountries, fetchCurrency } = props;
  const [amount, setAmount] = useState("");
  const [country, setCountry] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [period, setPeriod] = useState("1q");
  const [periodType, setPeriodType] = useState("quarter");
  const [year, setYear] = useState(years[0].value);
  const [anotherCurrency, setAnotherCurrency] = useState("eur");
  const [checkAnotherCurrency, setCheckAnotherCurrency] = useState(false);
  const [, setPaymentStatus] = useState("");
  const [, setCountryCode] = useState(undefined);
  const [orderResult] = useState(undefined);
  const [actionDialogs, setActionDialogs] = useState({
    pay: { open: false },
    notification: { open: false },
  });

  useEffect(() => {
    let cancelled = false;
    fetchRegNumbers(true)
      .then((list) => {
        if (cancelled) {
          return;
        }
        if (Array.isArray(list) && list.length > 0) {
          setCountry((prev) => prev || list[0].country);
          setCurrency((prev) => prev || list[0].currency);
        }
      })
      .catch(_.noop);
    fetchCountries();
    fetchCurrency();
    return () => {
      cancelled = true;
    };
  }, [fetchCountries, fetchCurrency, fetchRegNumbers]);

  const paymentСurrencies = useMemo(
    () => [
      { value: "eur", label: t("vatPayment.eur") },
      { value: "usd", label: t("vatPayment.usd") },
      { value: "gbr", label: t("vatPayment.gbr") },
    ],
    [t]
  );

  const periods = useMemo(
    () => [
      { type: "quarter", value: "1q", label: t("vatPayment.q1") },
      { type: "quarter", value: "2q", label: t("vatPayment.q2") },
      { type: "quarter", value: "3q", label: t("vatPayment.q3") },
      { type: "quarter", value: "4q", label: t("vatPayment.q4") },
      { type: "month", value: "1", label: t("vatPayment.jan") },
      { type: "month", value: "2", label: t("vatPayment.feb") },
      { type: "month", value: "3", label: t("vatPayment.mar") },
      { type: "month", value: "4", label: t("vatPayment.apr") },
      { type: "month", value: "5", label: t("vatPayment.may") },
      { type: "month", value: "6", label: t("vatPayment.june") },
      { type: "month", value: "7", label: t("vatPayment.july") },
      { type: "month", value: "8", label: t("vatPayment.aug") },
      { type: "month", value: "9", label: t("vatPayment.sept") },
      { type: "month", value: "10", label: t("vatPayment.oct") },
      { type: "month", value: "11", label: t("vatPayment.nov") },
      { type: "month", value: "12", label: t("vatPayment.dec") },
    ],
    [t]
  );

  const handleActionDialogsClose = useCallback((name) => {
    setActionDialogs((prev) => {
      const next = { ...prev };
      next[name] = { ...(next[name] || {}), open: false };
      next.cellData = {};
      return next;
    });
  }, []);

  const handleActionDialogsOpen = useCallback((name, data = {}) => {
    setActionDialogs((prev) => {
      const next = { ...prev };
      next[name] = { ...(next[name] || {}), open: true, data };
      return next;
    });
  }, []);

  const renderPayDialog = () => {
    const { data } = actionDialogs.pay || {};
    return (
      data && (
        <div className="pay -- 9999">
          <Modal
            title={t("vatPayment.orderVP")}
            visible={actionDialogs.pay.open}
            onClose={() => handleActionDialogsClose("pay")}
            content={
              <Payment
                service={"VAT payment"}
                sum={amount}
                currency={currency}
                comment={`${t("vatPayment.vatPayment")}: ${country}, ${Number.parseInt(
                  period,
                  16
                )} ${periodType} ${year}, ${t("vatPayment.paymentCurrLC")}: ${anotherCurrency}`}
                pplButtonId={""}
                closeDialog={handleActionDialogsClose}
                openNextDialog={handleActionDialogsOpen}
                currentDialog='pay'
                nextDialog='notification'
              />
            }
          />
        </div>
      )
    );
  };

  const renderNotificationDialog = () => (
    <Modal
      title={t("vatPayment.payment")}
      contentOnCenter
      visible={actionDialogs.notification.open}
      onClose={() => {
        handleActionDialogsClose("notification");
      }}
      description={<div style={{ maxWidth: "450px" }}>{t("vatReg.thankForReq")}</div>}
      footer={
        <Button
          primary
          onClick={() => {
            setAmount(undefined);
            setCountry(regNumbers ? regNumbers[0].country : undefined);
            setCurrency(regNumbers ? regNumbers[0].currency : undefined);
            setPeriod("1q");
            setPeriodType("quarter");
            setYear(years[0].value);
            setAnotherCurrency(paymentСurrencies[0].value);
            setCheckAnotherCurrency(false);
            handleActionDialogsClose("notification");
          }}
        >
          {t("vatPayment.ok")}
        </Button>
      }
    ></Modal>
  );

    const regCountries = [];
    if (regNumbers) {
      regNumbers.forEach((element) => {
        regCountries.push(element.country);
      });
    }

    let optionsCountries = [];
    _.map(regCountries, (country) => {
      optionsCountries.push({
        key: `${country} + 'cntr'`,
        label: country,
        value: country,
      });
    });
    optionsCountries = _.uniqBy(optionsCountries, "key");

    return (
      <Page
        separator
        fullWidth
        title={
          <GoBackPageNavigation content={
            <Text variant='heading3xl' as='span'>
             {t("vatPayment.orderVP")}
            </Text>
          } />
        }
      >
        <PageHelmet title={t("vatPayment.vatPayment")} />
        <Layout>
          <Layout.AnnotatedSection
            title={t("vatPayment.vatPaymentForm")}
            description={
              <Text variant="bodyMd" color="critical">
                {t("vatPayment.fillParam")}
              </Text>
            }
          >
            <Layout.Section>
              {fetchingRegNumbers ? (
                <Card sectioned>
                  <Spinner />
                </Card>
              ) : (
                <Card sectioned>
                  {_.isEmpty(regCountries) && (
                    <div>
                      {t("vatPayment.addVatRegData")}{" "}
                      <a className='Polaris-Link' href='/tax-setup'>
                        {t("vatPayment.here")}
                      </a>
                    </div>
                  )}
                  {!_.isEmpty(regCountries) && !orderResult && (
                    <div>
                      <FormLayout>
                        <FormLayout.Group>
                          <Select
                            id='formCountry'
                            name='from_country'
                            label={`${t("vatPayment.country")}`}
                            options={optionsCountries}
                            onChange={(value) => {
                              setCountry(value);
                              setPaymentStatus(undefined);
                              regNumbers.forEach((element) => {
                                if (element.country === value) {
                                  setCurrency(element.currency);
                                }
                              });
                              optionsCountries.forEach((element) => {
                                if (element.name === value) {
                                  setCountryCode(element.code);
                                }
                              });
                            }}
                            value={country}
                          />
                          <Select
                            id='fromYear'
                            name='from_year'
                            label={`${t("vatPayment.year")}:`}
                            options={years}
                            onChange={(value) => {
                              setYear(value);
                              setPaymentStatus(undefined);
                            }}
                            value={year}
                          />
                        </FormLayout.Group>
                        <FormLayout.Group>
                          <TextField
                            label={t("vatPayment.vatSum")}
                            suffix={currency}
                            value={amount}
                            onChange={(value) =>
                              (setAmount(value.replace(/[^\+\d]/g, "")), setPaymentStatus(undefined))
                            }
                          />
                          <Select
                            id='fromPeriod'
                            name='from_period'
                            label={`${t("vatPayment.period")}:`}
                            options={periods}
                            onChange={(value) => {
                              let periodType = "";
                              if (value.includes("q")) {
                                periodType = "quarter";
                              } else {
                                periodType = "month";
                              }
                              setPeriod(value);
                              setPeriodType(periodType);
                              setPaymentStatus(undefined);
                            }}
                            value={period}
                          />
                        </FormLayout.Group>
                        <FormLayout.Group>
                          <Checkbox
                            checked={checkAnotherCurrency}
                            label={t("vatPayment.wantReq")}
                            onChange={(checkAnotherCurrency) => {
                              setCheckAnotherCurrency(checkAnotherCurrency);
                            }}
                          />
                          {checkAnotherCurrency && (
                            <Select
                              id='anotherCurrency'
                              name='another_currency'
                              value={anotherCurrency}
                              placeholder={t("taxReturnsPages.common.select")}
                              label={`${t("vatPayment.paymentCurr")}:`}
                              options={paymentСurrencies}
                              onChange={(anotherCurrency) => {
                                setAnotherCurrency(anotherCurrency);
                              }}
                            />
                          )}
                        </FormLayout.Group>
                      </FormLayout>
                      <br />
                      <Button
                        variant={'primary'}
                        onClick={() => handleActionDialogsOpen("pay", {})}
                        disabled={
                          !(
                            country &&
                            currency &&
                            period &&
                            year
                          )
                        }
                      >
                        {t("vatPayment.order")}
                      </Button>
                    </div>
                  )}
                </Card>
              )}
            </Layout.Section>
          </Layout.AnnotatedSection>
        </Layout>
        {renderPayDialog()}
        {renderNotificationDialog()}
      </Page>
    );
}

const mapStateToProps = (state) => ({
  countries: state.other.countries,
  currency: state.other.currency,
  regNumbers: state.vatRegistration.regNumbers,
  fetchingRegNumbers: state.vatRegistration.fetching,
  defaultLanguage: state.user.defaultLanguage,
});

const mapDispatchToProps = (defaultDispatch) => {
  const dispatch = createCustomDispatch(defaultDispatch);

  return {
    fetchRegNumbers: (active) => dispatch(fetchRegNumbers(active)),
    fetchCountries: () => dispatch(fetchCountries()),
    fetchCurrency: () => dispatch(fetchCurrency()),
  };
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(VATPayment))
);
